import React, { useMemo, useContext } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from 'bonfire-kindling';

import axios from 'helpers/axios';
import { ToastContext } from 'contexts';
import { PROJECTS_PINNED } from 'constants/apis';
import { getLocations } from 'helpers/locations';
import { TimeRemaining, OpportunitySharedActions } from 'components';
import { useProjectClosingDate } from '../../../../hooks/useProjectClosingDate';
import { useIframeLogin, getBonfireOpportunityPath } from '../../../../hooks/useIframeLogin';
import { DASHBOARD_FOLLOWED_PROJECTS_COUNT_QUERY_KEY } from '../../../hooks';
import { DASHBOARD_RECOMMENDED_PROJECTS_QUERY_KEY } from '.';

const RecommendedOpportunity = ({
  Locations,
  DateClose,
  ProjectID,
  ProjectUUID,
  ProjectName,
  Organization,
  ExternalLink,
  IsFollowing,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { triggerToast } = useContext(ToastContext);
  const { isClosingSoon, remaining } = useProjectClosingDate(DateClose);
  const { summary: locationsSummary } = useMemo(() => getLocations(Locations), [Locations]);
  const { logInToOrganizationDomain, handleLoadGoToProjectIframe, iframeRef, iframeSrc } =
    useIframeLogin();

  const { mutate } = useMutation(
    ({ currentIsFollowing }) =>
      currentIsFollowing
        ? axios.delete(PROJECTS_PINNED, { data: { ProjectUUID } })
        : axios.post(PROJECTS_PINNED, { ProjectUUID }),
    {
      onMutate: async ({ currentIsFollowing }) => {
        await Promise.all([
          queryClient.cancelQueries(DASHBOARD_RECOMMENDED_PROJECTS_QUERY_KEY),
          queryClient.cancelQueries(DASHBOARD_FOLLOWED_PROJECTS_COUNT_QUERY_KEY),
        ]);

        const previousProjectsSnapshot = queryClient.getQueryData(
          DASHBOARD_RECOMMENDED_PROJECTS_QUERY_KEY
        );
        const previousFollowedProjectsCountSnapshot = queryClient.getQueryData(
          DASHBOARD_FOLLOWED_PROJECTS_COUNT_QUERY_KEY
        );

        queryClient.setQueryData(DASHBOARD_RECOMMENDED_PROJECTS_QUERY_KEY, previousProjects =>
          previousProjects.map(project =>
            project.ProjectUUID === ProjectUUID
              ? { ...project, IsFollowing: !currentIsFollowing }
              : project
          )
        );
        queryClient.setQueryData(
          DASHBOARD_FOLLOWED_PROJECTS_COUNT_QUERY_KEY,
          previousPinnedCount => ({
            totalPinned: currentIsFollowing
              ? Math.abs(previousPinnedCount.totalPinned - 1)
              : Math.abs(previousPinnedCount.totalPinned + 1),
          })
        );

        return { previousProjectsSnapshot, previousFollowedProjectsCountSnapshot };
      },
      onError: (error, variables, context) => {
        queryClient.setQueryData(
          DASHBOARD_RECOMMENDED_PROJECTS_QUERY_KEY,
          context.previousProjectsSnapshot
        );
        queryClient.setQueryData(
          DASHBOARD_FOLLOWED_PROJECTS_COUNT_QUERY_KEY,
          context.previousFollowedProjectsCountSnapshot
        );
        triggerToast({ message: t('generic_error') });
      },
      onSuccess: ({ data }) => {
        if (
          data.message !== 'Success: Pinned project' &&
          data.message !== 'Success: Unpinned project'
        ) {
          // Unexpected response, just invalidate the queries instead.
          queryClient.invalidateQueries(DASHBOARD_RECOMMENDED_PROJECTS_QUERY_KEY);
          queryClient.invalidateQueries(DASHBOARD_FOLLOWED_PROJECTS_COUNT_QUERY_KEY);
        }
      },
    }
  );

  return (
    <>
      <Box
        component="li"
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, md: 4 },
          border: theme => `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Box
            sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 0%', mr: { xs: 2, md: 0 } }}
          >
            <Typography variant="h3" sx={{ fontSize: '1rem', mb: 2 }}>
              {ProjectName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img
                alt={t('dashboard.overview.recommended_opportunities.org_logo', {
                  organization: Organization.OrganizationName,
                })}
                src={Organization.OrganizationLogoURL}
                style={{ marginRight: '1rem', width: '50px', objectFit: 'contain' }}
              />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {Organization.OrganizationName}
                </Typography>
                <Typography variant="body2">{locationsSummary}</Typography>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: 'block',
              overflow: 'visible',
              flex: { md: '1 1 0%' },
              textAlign: 'center',
            }}
          >
            <TimeRemaining
              label={t('dashboard.overview.recommended_opportunities.days_left', {
                count: remaining.days,
              })}
              value={isClosingSoon ? remaining.hours : remaining.days}
            />
          </Box>
        </Box>
        <Box sx={{ flex: { xs: '1 1 0%', md: '1 1 50%' }, width: '100%', mt: { xs: 3, md: 0 } }}>
          <Box sx={{ width: { xs: '100%', md: 'max-content' }, ml: { xs: 0, md: 'auto' } }}>
            <OpportunitySharedActions
              ExternalLink={ExternalLink}
              IsBonfireOrganization={Organization.IsBonfireOrganization}
              openBonfireOpportunity={() => {
                logInToOrganizationDomain(
                  `https://${Organization.Domain}`,
                  getBonfireOpportunityPath(ProjectID)
                );
              }}
              isFollowing={IsFollowing}
              toggleIsFollowing={() => mutate({ currentIsFollowing: IsFollowing })}
              sx={{
                mb: { xs: 0 },
                p: { xs: 0, md: theme => `0 ${theme.spacing(3)}` },
                flexDirection: { xs: 'row', sm: 'row-reverse', md: 'column' },
                justifyContent: { xs: 'space-between', md: 'flex-start' },
              }}
            />
          </Box>
        </Box>
      </Box>
      {iframeSrc && (
        <iframe
          style={{ display: 'none' }}
          ref={iframeRef}
          title={t('dashboard.organization_iframe_title', {
            organization: Organization.OrganizationName,
          })}
          src={iframeSrc}
          onLoad={handleLoadGoToProjectIframe}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default RecommendedOpportunity;
